/*
 * VNCmail : A whole new experience in enterprise email communication.
 * Copyright (C) 2015-2020 VNC – Virtual Network Consult AG (info@vnc.biz)
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, version 3 of the License.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program. Look for COPYING file in the top folder.
 * If not, see http://www.gnu.org/licenses/.
 */

import { Component, HostListener, Inject, OnDestroy, OnInit } from "@angular/core";
import { filter, Subject, switchMap, take, takeUntil } from "rxjs";
import { MatDialogRef, MAT_DIALOG_DATA } from "@angular/material/dialog";
import { BehaviorSubject } from "rxjs";
import { filesize } from "filesize";
import { FileInfo, OwnCloudService } from "src/app/shared/owncloud.service";
import { MailService } from "src/app/mail/shared/services/mail-service";
import { ConfigService } from "src/app/config.service";
import { environment } from "src/environments/environment";
import { MailUtils } from "src/app/mail/utils/mail-utils";
import moment from "moment";

@Component({
  selector: "vp-owncloud-upload",
  styleUrls: ["./owncloud-upload.scss"],
  templateUrl: "./owncloud-upload.html"
})
export class OwncloudUploadDialogComponent implements OnInit, OnDestroy {
  files$ = new BehaviorSubject<FileInfo[]>([]);
  currentFolder: any;
  file: any;
  isLoading: boolean;
  lang = localStorage.getItem("portalLanguage") || "en";
  locale: any;
  private isAlive$ = new Subject<boolean>();
  isPending: any;
  isLoggedIn: boolean;
  newFolder: any;
  uploadFromChat: any;
  constructor(
    private configService: ConfigService,
    private ownCloudService: OwnCloudService,
    private mailService: MailService,
    private dialog: MatDialogRef<OwncloudUploadDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any) {
      if (this.data && this.data.uploadFromChat) {
        this.uploadFromChat = this.data.uploadFromChat;
      } else {
        this.file = this.data.file;
      }
  }

  ngOnInit() {
    this.ownCloudService.isLoggedIn().pipe(filter(v => !!v), take(1)
    , switchMap(() => {
      this.isLoading = true;
      return this.ownCloudService.listAllFiles("/");
    })).subscribe(v => {
      this.isLoading = false;
      this.files$.next(v.filter(v => !!v.name));
    });
    this.ownCloudService.reload$.asObservable().pipe(takeUntil(this.isAlive$)).subscribe(v => {
      if (!!v) {
        this.reloadData();
      }
    });
    this.configService.currentLanguage.pipe(takeUntil(this.isAlive$)).subscribe((lang: string) => {
      this.lang = lang;
    });
    setTimeout(() => {
      this.home();
    }, 300);
    if (environment.isCordova) {
      StatusBar.backgroundColorByHexString("#000000");
      StatusBar.styleBlackTranslucent();
    }
  }

  getFileSize(size) {
    return filesize(size, { locale: this.lang });
  }

  getTimeAgo(date: any) {
    return MailUtils.getTimeAgo(date, this.lang);
  }

  backPreviousFolder() {
    const folders = this.currentFolder.path.split("/");
    folders.pop();
    const path = folders.join("/");
    if (path === "" || path === "/") {
      this.currentFolder = null;
      this.home();
    } else {
      this.openFolder({ type: "dir", path: path, name: path.slice(path.lastIndexOf("/") + 1, path.length) });
    }
  }

  async openFolder(file) {
    if (this.isPending) {
      return;
    }
    if (file.type === "dir") {
      this.currentFolder = file;
      this.isLoading = true;
      this.files$.next([]);
      this.ownCloudService.listAllFiles(file.path).subscribe(v => {
        this.currentFolder = v[0];
        this.files$.next(v.slice(1, v.length));
        this.isLoading = false;
      });
    }
  }

  home() {
    this.isLoading = true;
    this.currentFolder = null;
    this.files$.next([]);
    this.ownCloudService.listAllFiles("/").subscribe(v => {
      this.isLoading = false;
      this.files$.next(v.filter(v => v.name));
    });
  }

  ngOnDestroy() {
    this.isAlive$.next(false);
    this.isAlive$.complete();
    if (document.querySelector(".dialog-backdrop") !== null) {
      (<HTMLElement>document.querySelector(".dialog-backdrop")).style.display = "none";
    }
    if (environment.isCordova) {
      StatusBar.backgroundColorByHexString("#317bbc");
    }
  }

  @HostListener("document:keydown.esc")
  cancel(): void {
    this.dialog.close();
  }

  send() {
    let path = "/";
    if (this.currentFolder) {
      path = this.currentFolder.path + "/";
    }

    if (this.file) {
      this.mailService.openSnackBarWithTranslation("SENDING_FILE_TO_OWNCLOUD");
      this.ownCloudService.worker.postMessage(JSON.stringify({action: "download", path, url: this.file.url}));
      this.dialog.close({shared: true});
    } else if (this.uploadFromChat) {
      this.dialog.close({currentFolder: this.currentFolder, selectedFolder: true});
    }
  }

  createFolder() {
    const name = "New Folder " + new Date().getTime();
    let path = "/" + name;
    if (this.currentFolder) {
      path = this.currentFolder.path + path;
    }
    this.newFolder = {
      type: "dir",
      name,
      path
    };
    setTimeout(() => {
      if (document.querySelector(".is-edited input")) {
        (<HTMLInputElement>document.querySelector(".is-edited input")).focus();
      }
    }, 500);
  }

  get isMobileSize () {
    return MailUtils.isMobileSize();
  }

  renameFolder(name) {
    if (name.indexOf("/") !== -1) {
      this.mailService.openSnackBarWithTranslation("FILE_NAME_CANNOT_CONTAIN");
    } else {
      this.newFolder = null;
      let path = "/" + name;
      if (this.currentFolder) {
        path = this.currentFolder.path + path;
      }
      this.ownCloudService.oc.files.createFolder(path).then(v => {
        if (v) {
          this.openFolder({name, path, type: "dir"});
          this.mailService.openSnackBarWithTranslation("FOLDER_HAS_BEEN_CREATED", { folderName: path.replace("/", "") });
        }
      }).catch(() => {
        this.newFolder = null;
        this.mailService.openSnackBarWithTranslation("CANNOT_RENAME");
      });
    }
  }

  reloadData() {
    if (this.currentFolder) {
      this.openFolder(this.currentFolder);
    } else {
      this.home();
    }
  }

  cancelRename() {
    this.newFolder = null;
  }

  addToFavorite(file) {
    this.ownCloudService.toggleFavorite(file.path, true).then(() => {
      file.starred = true;
      this.updateFileData(file);
    });
  }

  removeFromFavorite(file) {
    this.ownCloudService.toggleFavorite(file.path, false).then(() => {
      file.starred = false;
      this.updateFileData(file);
    });
  }

  updateFileData(file) {
    if (this.files$.value.find(v => v.fileId === file.id)) {
      const newFiles = this.files$.value;
      newFiles.splice(newFiles.findIndex(v => v.fileId === file.id), 1, file);
      this.files$.next(newFiles);
    }
  }

}
